// Preview ao vivo do editor (§4.5): variáveis substituídas pelos
// exemplos do contrato, HTML SEMPRE sanitizado (DOMPurify — padrão
// do repo), toggle desktop/mobile e bolha de chat para WHATSAPP.
// Variáveis não declaradas aparecem marcadas com ⚠.
import DOMPurify from "dompurify"
import { MonitorIcon, SmartphoneIcon } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type {
  NotificationSearchChannelType,
  NotificationTemplateVariableType,
} from "@/types/notification-center"

import {
  buildWhatsAppPreviewText,
  renderTemplatePreview,
} from "../template-editor-utils"

const editorPrefix =
  "pages.notificationCenter.templateEditorPage"

type TemplatePreviewProps = {
  channel: NotificationSearchChannelType
  source: string
  variables: NotificationTemplateVariableType[]
}

type ViewportType = "desktop" | "mobile"

export const TemplatePreview = ({
  channel,
  source,
  variables,
}: TemplatePreviewProps) => {
  const { t } = useTranslation()
  const [viewport, setViewport] =
    useState<ViewportType>("desktop")

  const isWhatsApp = channel === "WHATSAPP"

  return (
    <section
      aria-label={t(`${editorPrefix}.preview.title`)}
      className="flex h-full flex-col gap-2"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {t(`${editorPrefix}.preview.title`)}
        </h3>
        <div
          className="flex gap-1"
          role="group"
          aria-label={t(
            `${editorPrefix}.preview.viewportToggle`
          )}
        >
          <Button
            type="button"
            variant={
              viewport === "desktop" ? "secondary" : "ghost"
            }
            size="sm"
            aria-pressed={viewport === "desktop"}
            onClick={() => setViewport("desktop")}
          >
            <MonitorIcon
              className="mr-1 size-4"
              aria-hidden="true"
            />
            {t(`${editorPrefix}.preview.desktop`)}
          </Button>
          <Button
            type="button"
            variant={
              viewport === "mobile" ? "secondary" : "ghost"
            }
            size="sm"
            aria-pressed={viewport === "mobile"}
            onClick={() => setViewport("mobile")}
          >
            <SmartphoneIcon
              className="mr-1 size-4"
              aria-hidden="true"
            />
            {t(`${editorPrefix}.preview.mobile`)}
          </Button>
        </div>
      </div>
      <div className="flex flex-1 justify-center overflow-auto rounded-md border bg-muted/30 p-4">
        <div
          data-testid="template-preview-frame"
          className={cn(
            "w-full transition-all",
            viewport === "mobile" && "max-w-[375px]"
          )}
        >
          {isWhatsApp ? (
            <div className="flex flex-col gap-2 rounded-lg bg-[#e5ddd5] p-4">
              <div className="max-w-[85%] self-start rounded-lg rounded-tl-none bg-white p-3 text-sm shadow-sm">
                <p className="whitespace-pre-wrap break-words">
                  {buildWhatsAppPreviewText(
                    source,
                    variables
                  )}
                </p>
                <span className="mt-1 block text-right text-[10px] tabular-nums text-muted-foreground">
                  12:00
                </span>
              </div>
            </div>
          ) : (
            <div
              className="rounded-md bg-white p-4 shadow-sm"
              data-testid="template-preview-html"
              // Preview sanitizado — nunca renderizar HTML cru
              // (XSS via template comprometido).
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  renderTemplatePreview(source, variables)
                ),
              }}
            />
          )}
        </div>
      </div>
    </section>
  )
}
